import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { can } from '../../../utils/permissions';
import { getFolderPath, getFolderTrail, getMoveDestinations } from './modelFolders';
import './ModelFolders.css';
import ModelDeleteDialog from './ModelDeleteDialog';
import ModelActionIcon from './ModelActionIcon';

const countPinyes = count => `${count} ${count === 1 ? 'pinya' : 'pinyes'}`;
const countFolders = count => `${count} ${count === 1 ? 'subcarpeta' : 'subcarpetes'}`;

function FolderDialog({ title, initialName, destination, folders, movingId, onConfirm, onClose, busy }) {
    const [value, setValue] = useState(initialName !== undefined ? initialName : destination || '');
    const [error, setError] = useState('');
    const ref = useRef(null);
    useEffect(() => {
        const previous = document.activeElement;
        ref.current.showModal();
        return () => { if (previous?.isConnected) previous.focus(); };
    }, []);
    return createPortal(
        <dialog ref={ref} className="model-folder-dialog" aria-label={title} onCancel={e => { e.preventDefault(); if (!busy) onClose(); }}>
            <form className="model-folder-dialog-form" onSubmit={async e => {
                e.preventDefault();
                try { await onConfirm(initialName !== undefined ? value.trim() : value || null); onClose(); }
                catch (err) { setError(err.message); }
            }}>
                <h3>{title}</h3>
                {initialName !== undefined ? <label className="model-folder-dialog-field">Nom de la carpeta
                    <input type="text" className="model-folder-dialog-control" autoFocus required maxLength={100} value={value} onChange={e => setValue(e.target.value)} />
                </label> : <label className="model-folder-dialog-field">Carpeta de destí
                    <select className="model-folder-dialog-control" autoFocus value={value} onChange={e => setValue(e.target.value)}>
                        <option value="">Sense carpeta (arrel)</option>
                        {getMoveDestinations(folders, movingId).map(folder => <option key={folder.id} value={folder.id}>{getFolderPath(folders, folder.id)}</option>)}
                    </select>
                </label>}
                {error && <p role="alert">{error}</p>}
                <div className="model-folder-dialog-actions">
                    <button type="button" onClick={onClose} disabled={busy}>Cancel·la</button>
                    <button type="submit" className="model-folder-primary" disabled={busy || (initialName !== undefined ? !value.trim() : value === (destination || ''))}>{busy ? 'Desant…' : initialName !== undefined ? 'Desa' : 'Mou'}</button>
                </div>
            </form>
        </dialog>, document.body
    );
}

export default function ModelFolderBrowser({ models, userInfo, visibleNames, selected, setSelected }) {
    const [dialog, setDialog] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const { data, folderId, navigate, mutate, busy, error } = models;
    const folders = data?.folders || [];
    const children = folders.filter(f => (f.parentId || null) === folderId).sort((a, b) => a.nom.localeCompare(b.nom, 'ca'));
    const canEdit = can(userInfo, 'EDIT_PROVA');
    const archive = async folder => {
        try { await mutate('archive', { id: folder.id }); } catch (_) { /* Error shown below. */ }
    };
    const moveSelected = () => setDialog({
        title: `Mou ${selected.length} ${selected.length === 1 ? 'pinya' : 'pinyes'}`,
        destination: folderId,
        onConfirm: async target => { await mutate('move', { proves: selected, folderId: target }); setSelected([]); },
    });
    return <section className="model-folders" aria-label="Carpetes de Pinyes Model">
        <nav aria-label="Ruta de carpetes" className="model-folder-breadcrumbs">
            <button onClick={() => navigate(null)} aria-current={folderId === null ? 'page' : undefined}>Pinyes Model</button>
            {getFolderTrail(folders, folderId).map(folder => <span key={folder.id}> / <button onClick={() => navigate(folder.id)} aria-current={folderId === folder.id ? 'page' : undefined}>{folder.nom}</button></span>)}
        </nav>
        <div className="model-folder-toolbar">
            {can(userInfo, 'ADD_PROVA') && <button disabled={busy || !data} onClick={() => setDialog({ title: folderId ? 'Crea una subcarpeta' : 'Crea una carpeta', initialName: '', onConfirm: nom => mutate('create', { nom, parentId: folderId }) })}>{folderId ? '＋ Subcarpeta' : '＋ Carpeta'}</button>}
            {canEdit && visibleNames.length > 0 && <button disabled={busy} onClick={() => setSelected(selected.length === visibleNames.length ? [] : [...visibleNames])}>{selected.length === visibleNames.length ? 'Desmarca totes' : 'Selecciona totes'}</button>}
            {canEdit && selected.length > 0 && <button className="model-folder-primary" disabled={busy} onClick={moveSelected}>Mou a… ({selected.length})</button>}
        </div>
        {error && <div role="alert" className="model-folder-error">{error} <button onClick={models.refresh}>Actualitza</button></div>}
        {!data && !error && <p role="status">Carregant carpetes…</p>}
        <div className="model-folder-list">
            {children.map(folder => <div className="model-folder-row" key={folder.id}>
                <button className="model-folder-open" onClick={() => navigate(folder.id)}>
                    <span aria-hidden="true" className="model-folder-icon">📁</span>
                    <span className="model-folder-caption"><strong>{folder.nom}</strong><small>{countPinyes(folder.pinyaCount)}{folder.folderCount > 0 ? ` · ${countFolders(folder.folderCount)}` : ''}</small></span>
                    <span aria-hidden="true" className="model-folder-chevron">›</span>
                </button>
                {folder.canManageContent && (canEdit || can(userInfo, 'DELETE_PROVA')) && <details className="model-folder-menu">
                    <summary aria-label={`Accions de ${folder.nom}`}>•••</summary>
                    <div>
                        {canEdit && <button disabled={busy} onClick={() => setDialog({ title: 'Canvia el nom', initialName: folder.nom, onConfirm: nom => mutate('edit', { id: folder.id, nom }) })}><ModelActionIcon action="rename" /><span>Canvia el nom</span></button>}
                        {canEdit && <button disabled={busy} onClick={() => setDialog({ title: `Mou «${folder.nom}»`, destination: folder.parentId, movingId: folder.id, onConfirm: parentId => mutate('edit', { id: folder.id, parentId }) })}><ModelActionIcon action="move" /><span>Mou a…</span></button>}
                        {can(userInfo, 'DELETE_PROVA') && <>
                            <button disabled={busy} onClick={() => archive(folder)}><ModelActionIcon action="archive" /><span>Arxiva</span></button>
                            <button className="model-folder-danger-text" disabled={busy} onClick={() => setDeleting(folder)}><ModelActionIcon action="delete" /><span>Elimina definitivament…</span></button>
                        </>}
                    </div>
                </details>}
            </div>)}
        </div>
        {data && !children.length && !visibleNames.length && <p className="model-folder-empty">{folderId ? 'Aquesta carpeta està buida.' : 'Encara no hi ha pinyes ni carpetes.'}</p>}
        {dialog && <FolderDialog {...dialog} folders={folders} busy={busy} onClose={() => setDialog(null)} />}
        {deleting && <ModelDeleteDialog models={models} target={{ kind: 'folder', id: deleting.id }} name={deleting.nom}
            description={`S’eliminarà la carpeta amb ${countPinyes(deleting.pinyaCount)} i ${countFolders(deleting.folderCount)}.`} onClose={() => setDeleting(null)} />}
    </section>;
}

export function ModelFolderTrash({ models, bundlesInfo, userInfo, legacyProves = [], onRestoreLegacy, onDeletedLegacy }) {
    const [notice, setNotice] = useState('');
    const [deleting, setDeleting] = useState(null);
    const restore = async item => {
        try {
            if (item.kind === 'legacy') { onRestoreLegacy(item.castell, item.versio); return; }
            const result = await models.mutate('restore', { id: item.id });
            setNotice(result.restoredToRoot ? 'Recuperat a l’arrel perquè la carpeta original ja no està disponible.' : 'Contingut recuperat.');
        } catch (_) { /* Error shown by the folder browser. */ }
    };
    return <div className="model-folder-trash">
        <p>Les pinyes i carpetes arxivades es poden recuperar. No apareixen a les propostes d’importació de plantilles.</p>
        {notice && <p role="status">{notice}</p>}
        {[...(models.data?.trash || []), ...legacyProves.map(p => ({ ...p, id: `legacy-${p.fullName}`, kind: 'legacy', nom: p.fullName }))].map(item => {
            const bundle = bundlesInfo.find(b => b.id === item.fullName?.split('.')[0]);
            const name = item.kind === 'folder' ? item.nom : bundle?.nom || item.nom;
            return <div className="model-folder-trash-row" key={item.id}>
                <div><strong>{item.kind === 'folder' ? `📁 ${item.nom}` : bundle?.nom || item.nom}</strong>
                    <small>{item.originalPath || 'Sense carpeta'} · {new Date(item.deletedAt).toLocaleDateString('ca-ES')}</small>
                    {item.kind === 'folder' && <small>{countPinyes(item.pinyaCount)} · {countFolders(Math.max(0, item.folderCount - 1))}</small>}
                </div>
                <div className="model-archive-actions">
                    <button disabled={models.busy} onClick={() => restore(item)}>{item.kind === 'folder' ? 'Recupera la carpeta' : 'Recupera la pinya'}</button>
                    {can(userInfo, 'DELETE_PROVA') && <button className="model-folder-danger-text" disabled={models.busy} onClick={() => setDeleting({ ...item, name })}>Elimina definitivament…</button>}
                </div>
            </div>;
        })}
        {deleting && <ModelDeleteDialog models={models} name={deleting.name}
            target={deleting.kind === 'legacy' ? { kind: 'legacy', name: deleting.fullName } : { kind: 'archive', id: deleting.id }}
            description={deleting.kind === 'folder' ? `S’eliminarà la carpeta arxivada amb ${countPinyes(deleting.pinyaCount)} i ${countFolders(Math.max(0, deleting.folderCount - 1))}.` : undefined}
            onDeleted={() => { setNotice('Contingut eliminat definitivament.'); if (deleting.kind === 'legacy') onDeletedLegacy?.(); }} onClose={() => setDeleting(null)} />}
    </div>;
}
